import { useState, type ReactNode } from 'react'

/**
 * Sprechblase für Diagramme – die Hover-Schicht, die jedes Diagramm hier hat.
 *
 * Optik aus dem Mockup (`.tooltip`): dunkle Fläche, 12 px, folgt dem Zeiger.
 *
 * **Auch am Tastaturfokus**, nicht nur am Zeiger. Leitplanke 3 verlangt, dass
 * alles per Tastatur erreichbar ist – ein Diagramm, dessen Werte nur die Maus
 * zeigt, erfüllt das nicht. `onFocus` setzt die Blase an die Mitte der
 * Markierung, damit sie beim Durchtabben an derselben Stelle steht wie beim
 * Zeigen.
 *
 * Die Blase **ergänzt** und trägt nie allein: jeder Wert steht zusätzlich am
 * Balken, in der Achse oder in der Tabellensicht.
 */
export function useHinweis() {
  const [stand, setStand] = useState<{ inhalt: ReactNode; x: number; y: number } | null>(null)

  /** Auf eine Markierung spreizen: `<button {...anMark(<>…</>)}>`. */
  function anMark(inhalt: ReactNode) {
    return {
      onMouseMove: (e: React.MouseEvent) => setStand({ inhalt, x: e.clientX, y: e.clientY }),
      onMouseLeave: () => setStand(null),
      onFocus: (e: React.FocusEvent<HTMLElement>) => {
        const r = e.currentTarget.getBoundingClientRect()
        setStand({ inhalt, x: r.left + r.width / 2, y: r.bottom })
      },
      onBlur: () => setStand(null),
    }
  }

  const feld = stand ? (
    <div
      role="tooltip"
      className="pointer-events-none fixed z-50 max-w-[270px] rounded-[9px] bg-ink px-[11px] py-2 text-xs text-white shadow-[0_6px_20px_rgba(0,0,0,.25)]"
      style={{
        left: Math.min(stand.x + 12, window.innerWidth - 290),
        top: stand.y + 14,
      }}
    >
      {stand.inhalt}
    </div>
  ) : null

  return { anMark, feld }
}

/**
 * Umschalter auf die Tabellensicht – das barrierefreie Zwilling jedes Diagramms.
 *
 * Zwei Gründe, warum das keine Kür ist:
 *
 * 1. Die Quellenfarbe von Work (`#1baf7a`) erreicht gegen Weiß nur 2,82:1 und
 *    liegt damit unter 3:1. Das ist zulässig – aber nur mit einem zweiten Weg
 *    zum Wert. Die Tabelle ist dieser Weg.
 * 2. Farbe allein darf nie die einzige Kodierung sein. Wer die Zeitleiste nicht
 *    nach Farben lesen kann, liest dieselben Zahlen hier als Text.
 */
export function TabellenUmschalter({
  offen,
  umschalten,
  diagramm,
}: {
  offen: boolean
  umschalten: () => void
  /**
   * Name des Diagramms, zu dem der Schalter gehört.
   *
   * Auf dem Leitstand stehen mehrere Umschalter, alle mit der Aufschrift „Als
   * Tabelle". Vorgelesen sind sie nicht unterscheidbar – und wer die Tabelle als
   * zweiten Weg zum Wert braucht (Punkt 1 oben), ist genau die Person, die auf
   * diesen Namen angewiesen ist.
   */
  diagramm: string
}) {
  return (
    <button
      type="button"
      onClick={umschalten}
      className="cursor-pointer rounded-lg px-2 py-1 text-[11px] font-semibold text-ink2 hover:bg-[#f3f2ef]"
      aria-expanded={offen}
      aria-label={offen ? `${diagramm} als Diagramm` : `${diagramm} als Tabelle`}
    >
      {offen ? 'Diagramm' : 'Als Tabelle'}
    </button>
  )
}
